import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "components/translations/useTranslation";
import { useSelector, useDispatch } from "react-redux";
import { Store } from "redux";
import * as reduxSelectors from "components/store/application.reducers";
import { getWorkflowsAmountLimits } from "components/workflows/workflows.action";
import * as Api from "utils/api/api.js";
import { launchDarkly } from "utils/launchDarkly";
import { Button, ButtonType } from "components/common/Button.component";
import { TextInput } from "components/common/TextInput.component";
import { QuantitySelector } from "components/common/QuantitySelector.component";
import Select from "components/common/select/Select.component";
import { PencilIcon, Trash2Icon } from "lucide-react";
import {
    ChangeAmountLimitProps,
    AmountLimit,
    AmountDetail,
    LimitsNamesProps,
    AmountDetailsProps,
} from "types/modals";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";

import "components/popup/ChangeAmountLimit.scss";

// Constants for tabIndex calculations
const TAB_INDICES_PER_LIMIT_ITEM = 3;
const TAB_INDICES_PER_AMOUNT_ITEM = 4;

// ─── LimitNameRow ────────────────────────────────────────────────────────────

const LimitNameRow = ({
    amount,
    isNew = false,
    selectedLimitId,
    tabIndexStart,
    getAmountDetails,
    deleteAmountLimit,
    saveAmountLimitName,
    cancelCallback,
    editButtonRef,
    inputRef,
}: LimitsNamesProps) => {
    const { translate } = useTranslation();
    const useNewApi = launchDarkly.userClient?.variation("new-amount-limit-api", false);

    const [editMode, setEditMode] = useState(isNew);
    const [inputValue, setInputValue] = useState(amount?.name ?? "");

    const enterEditMode = useCallback(() => {
        setEditMode(true);
        setInputValue(amount?.name ?? "");
    }, [amount]);

    const exitEditMode = useCallback(() => {
        if (isNew) {
            cancelCallback?.();
        } else {
            setEditMode(false);
        }
    }, [isNew, cancelCallback]);

    const save = useCallback(() => {
        let name = inputValue;
        if (name.length > 80) name = name.substring(0, 80);
        if (name) {
            const limitId = useNewApi ? amount?.id ?? null : amount?.limitId ?? null;
            saveAmountLimitName(limitId ?? null, name);
        }
        if (!isNew) setEditMode(false);
    }, [inputValue, isNew, saveAmountLimitName, useNewApi, amount]);

    const handleGetDetails = useCallback(() => {
        const limitId = useNewApi ? amount?.id : amount?.limitId;
        if (limitId != null) getAmountDetails(limitId);
    }, [amount, useNewApi, getAmountDetails]);

    const handleDelete = useCallback(
        (e: React.MouseEvent) => {
            const limitId = useNewApi ? amount?.id : amount?.limitId;
            e.stopPropagation();
            if (limitId != null) deleteAmountLimit(limitId, false);
        },
        [amount, useNewApi, deleteAmountLimit],
    );

    const isSelected = selectedLimitId === (useNewApi ? amount?.id : amount?.limitId);

    if (editMode) {
        return (
            <div className="row selected p-1">
                <div className="col-md-7 px-1 py-1 cropped-text">
                    <TextInput
                        id={`editLimit-input-${amount?.id ?? amount?.limitId ?? "new"}`}
                        autoFocus
                        value={inputValue}
                        tabIndex={tabIndexStart}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
                        ref={(el: HTMLElement | null) => {
                            if (inputRef) (inputRef as React.MutableRefObject<HTMLElement | null>).current = el;
                        }}
                    />
                </div>
                <div className="d-flex justify-content-end gap-8 px-0 py-1 col-md-5">
                    <Button
                        type={ButtonType.SECONDARY}
                        onClick={save}
                        tabIndex={tabIndexStart + 1}
                        text={translate("popUp.editLimit.save")}
                    />
                    <Button
                        type={ButtonType.SECONDARY}
                        onClick={exitEditMode}
                        tabIndex={tabIndexStart + 2}
                        text={translate("popUp.editLimit.cancel")}
                    />
                </div>
            </div>
        );
    }

    return (
        <div
            className={"row p-2" + (isSelected ? " selected" : "")}
            onClick={handleGetDetails}
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleGetDetails();
                }
            }}
            role="button"
            tabIndex={tabIndexStart + 2}
        >
            <div className="col-md-9 px-0 py-3 cropped-text">{amount?.name}</div>
            <div className="d-flex justify-content-end gap-8 px-0 col-md-3">
                <Button
                    type={ButtonType.GHOST}
                    iconOnly
                    icon={<PencilIcon />}
                    onClick={enterEditMode}
                    tabIndex={tabIndexStart}
                    ref={editButtonRef}
                    title={translate("popUp.editLimit.edit")}
                />
                <Button
                    type={ButtonType.GHOST}
                    iconOnly
                    icon={<Trash2Icon />}
                    onClick={handleDelete}
                    tabIndex={tabIndexStart + 1}
                    title={translate("popUp.editLimit.delete")}
                />
            </div>
        </div>
    );
};

// ─── AmountDetailRow ─────────────────────────────────────────────────────────

const AmountDetailRow = ({
    detail,
    isNew = false,
    id,
    allCurrencies,
    deleteCurrency,
    addCurrencyLimit,
    tabIndexStart,
    cancelCallback,
}: AmountDetailsProps) => {
    const { translate } = useTranslation();

    const [editMode, setEditMode] = useState(isNew);
    const [amount, setAmount] = useState<number | string>(detail?.amount ?? (isNew ? 1 : ""));
    const [currency, setCurrency] = useState(detail?.currencyCode ?? "");

    const exitEditMode = useCallback(() => {
        if (isNew) {
            cancelCallback?.();
        } else {
            setEditMode(false);
            setAmount(detail?.amount ?? "");
            setCurrency(detail?.currencyCode ?? "");
        }
    }, [isNew, cancelCallback, detail]);

    const handleSave = useCallback(() => {
        const trimmedCurrency = currency.trim();
        if (!trimmedCurrency) return;
        const numericAmount = Number(amount);
        const safeAmount = Number.isFinite(numericAmount) && numericAmount > 0 ? numericAmount : 1;
        addCurrencyLimit(id, trimmedCurrency, safeAmount);
        if (!isNew) setEditMode(false);
    }, [id, currency, amount, isNew, addCurrencyLimit]);

    const handleDeleteCurrency = useCallback(() => {
        if (detail) deleteCurrency(id, detail.currencyCode);
    }, [id, detail, deleteCurrency]);

    if (editMode) {
        const currencyOptions = allCurrencies.map((c) => ({ label: c, value: c }));
        return (
            <div className="row px-2 py-2">
                <div className={`col-md-3 px-0 ${isNew ? "py-1" : "py-3"}`}>
                    {isNew ? (
                        <Select
                            options={currencyOptions}
                            isClearable={false}
                            isSearchable
                            onChange={(opt: { value: string }) => setCurrency(opt.value)}
                            tabIndex={tabIndexStart}
                            menuPortalTarget={document.body}
                            menuPlacement="auto"
                            translate={translate}
                        />
                    ) : (
                        currency
                    )}
                </div>
                <div className="col-md-4 py-1 text-center">
                    <QuantitySelector
                        min={1}
                        value={
                            Number.isFinite(Number(amount)) && Number(amount) > 0
                                ? Number(amount)
                                : 1
                        }
                        strict
                        tabIndex={tabIndexStart + 1}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const v = Number(e.target.value);
                            setAmount(Number.isFinite(v) && v > 0 ? v : 1);
                        }}
                    />
                </div>
                <div className="d-flex justify-content-end gap-8 px-0 col-md-5">
                    <Button
                        type={ButtonType.SECONDARY}
                        onClick={handleSave}
                        tabIndex={tabIndexStart + 2}
                        text={translate("popUp.editLimit.save")}
                    />
                    <Button
                        type={ButtonType.SECONDARY}
                        onClick={exitEditMode}
                        tabIndex={tabIndexStart + 3}
                        text={translate("popUp.editLimit.cancel")}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="row p-2">
            <div className="col-md-3 px-0 py-3">{currency}</div>
            <div className="col-md-6 text-center py-3">{amount}</div>
            <div className="d-flex justify-content-end gap-8 px-0 col-md-3">
                <Button
                    type={ButtonType.GHOST}
                    iconOnly
                    icon={<PencilIcon />}
                    onClick={() => setEditMode(true)}
                    tabIndex={tabIndexStart}
                    title={translate("popUp.editLimit.edit")}
                />
                <Button
                    type={ButtonType.GHOST}
                    iconOnly
                    icon={<Trash2Icon />}
                    onClick={handleDeleteCurrency}
                    tabIndex={tabIndexStart + 1}
                    title={translate("popUp.editLimit.delete")}
                />
            </div>
        </div>
    );
};

// ─── ChangeAmountLimit modal ─────────────────────────────────────────────────

const ChangeAmountLimit = ({
    closeModal,
    loopFocus,
    errorCallback,
}: ChangeAmountLimitProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();
    const amountLimits: AmountLimit[] =
        useSelector((store: Store) => reduxSelectors.getWorkflowsAmountLimits(store)) ?? [];
    const currentContext: string = useSelector((store: Store) =>
        reduxSelectors.getUsersCurrentContextId(store),
    );

    const useNewAmountLimitApi = launchDarkly.userClient?.variation("new-amount-limit-api", false);

    const [selectedLimit, setSelectedLimit] = useState<number | null>(null);
    const [amountDetails, setAmountDetails] = useState<AmountDetail[]>([]);
    const [allCurrencies, setAllCurrencies] = useState<string[]>([]);
    const [showNewLimit, setShowNewLimit] = useState(false);
    const [showNewDetails, setShowNewDetails] = useState(false);
    const [warningMessage, setWarningMessage] = useState<string | null>(null);
    const [toDelete, setToDelete] = useState<number | null>(null);
    const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);

    const firstEditButtonRef = useRef<HTMLButtonElement | null>(null);
    const addLimitButtonRef = useRef<HTMLButtonElement | null>(null);
    const firstInputRef = useRef<HTMLElement | null>(null);

    // Focus management on mount
    useEffect(() => {
        if (firstInputRef.current) {
            firstInputRef.current.focus();
        } else if (firstEditButtonRef.current) {
            firstEditButtonRef.current.focus();
        } else if (addLimitButtonRef.current) {
            addLimitButtonRef.current.focus();
        }
    }, []);

    // Load currencies on mount
    useEffect(() => {
        Api.getAllCurrencies().then(
            (response: string[]) => setAllCurrencies(response),
            (error: unknown) => errorCallback({ error }),
        );
    }, [errorCallback]);

    const getAmountDetails = useCallback(
        (id: number) => {
            setAmountDetails([]);
            setSelectedLimit(id);
            Api.getAmountLimitDetails(id, currentContext).then(
                (response: AmountDetail[] | { amounts: Record<string, number> }) => {
                    if (useNewAmountLimitApi && (response as any)?.amounts) {
                        const normalized = Object.keys((response as any).amounts).map((key) => ({
                            amount: (response as any).amounts[key],
                            currencyCode: key,
                        }));
                        setAmountDetails(normalized);
                    } else {
                        setAmountDetails(response as AmountDetail[]);
                    }
                },
                (error: unknown) => errorCallback({ error }),
            );
        },
        [currentContext, useNewAmountLimitApi, errorCallback],
    );

    const deleteAmountLimit = useCallback(
        (id: number, confirm = false) => {
            Api.deleteAmountLimit(id, confirm, currentContext).then(
                () => {
                    setWarningMessage(null);
                    setSelectedLimit(null);
                    setAmountDetails([]);
                    dispatch(getWorkflowsAmountLimits(currentContext));
                },
                (error: any) => {
                    if (error.errorType === "CONFIRMATION") {
                        setConfirmDeleteVisible(true);
                        setToDelete(id);
                        setWarningMessage(error.errorMessages);
                    } else {
                        errorCallback({ error });
                    }
                },
            );
        },
        [currentContext, dispatch, errorCallback],
    );

    const confirmDelete = useCallback(() => {
        if (toDelete != null) deleteAmountLimit(toDelete, true);
    }, [toDelete, deleteAmountLimit]);

    const discardDelete = useCallback(() => {
        setToDelete(null);
        setWarningMessage(null);
        setConfirmDeleteVisible(false);
    }, []);

    const saveAmountLimit = useCallback(
        (id: number | null, value: string) => {
            if (id == null) return;
            Api.editAmountLimit(id, value, currentContext).then(
                () => dispatch(getWorkflowsAmountLimits(currentContext)),
                (error: unknown) => errorCallback({ error }),
            );
        },
        [currentContext, dispatch, errorCallback],
    );

    const addAmountLimit = useCallback(
        (_id: number | null, value: string) => {
            Api.addAmountLimit(value, currentContext).then(
                () => {
                    dispatch(getWorkflowsAmountLimits(currentContext));
                    setShowNewLimit(false);
                },
                (error: unknown) => errorCallback({ error }),
            );
        },
        [currentContext, dispatch, errorCallback],
    );

    const addCurrencyLimit = useCallback(
        (id: number, currency: string, amount: number | string) => {
            Api.addCurrencyAmountLimit(id, currency, amount).then(
                () => {
                    setShowNewDetails(false);
                    getAmountDetails(id);
                },
                (error: unknown) => errorCallback({ error }),
            );
        },
        [getAmountDetails, errorCallback],
    );

    const deleteCurrency = useCallback(
        (id: number, currency: string) => {
            Api.deleteCurrency(id, currency).then(
                () => getAmountDetails(id),
                (error: unknown) => errorCallback({ error }),
            );
        },
        [getAmountDetails, errorCallback],
    );

    // Tab index calculations
    const limitsCount = amountLimits?.length ?? 0;
    const detailsCount = amountDetails?.length ?? 0;
    const limitsSectionStart = MODAL_CONTENT_TAB_INDEX;
    const limitsAddButton =
        limitsSectionStart +
        limitsCount * TAB_INDICES_PER_LIMIT_ITEM +
        (showNewLimit ? TAB_INDICES_PER_LIMIT_ITEM : 0);
    const detailsSectionStart = limitsAddButton + 1;
    const detailsAddButton =
        detailsSectionStart +
        detailsCount * TAB_INDICES_PER_AMOUNT_ITEM +
        (showNewDetails ? TAB_INDICES_PER_AMOUNT_ITEM : 0);
    const okButton = detailsAddButton + 1;

    if (confirmDeleteVisible && warningMessage) {
        return (
            <div className="modal-warning">
                <p>{warningMessage}</p>
                <Button
                    type={ButtonType.PRIMARY}
                    onClick={confirmDelete}
                    text={translate("popUp.editLimit.confirmDelete")}
                    tabIndex={MODAL_CONTENT_TAB_INDEX}
                />
                <Button
                    type={ButtonType.SECONDARY}
                    onClick={discardDelete}
                    onBlur={loopFocus}
                    text={translate("popUp.cancel")}
                    tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                />
            </div>
        );
    }

    return (
        <>
            <div className="ga-modal__content" id="Edit-Limit">
                {/* Limits section */}
                <div className="actions-list col-md-12 px-2">
                    <div className="row px-0 mx-0">
                        <div className="col-md-9 px-0">{translate("popUp.editLimit.limit")}</div>
                        <div className="col-md-3 px-0 float-right text-end">{translate("roles.actions")}</div>
                    </div>
                    <div className="col-md-12 main-panel px-0 border-0">
                        {amountLimits?.map((amount, index) => (
                            <LimitNameRow
                                key={
                                    (amount as AmountLimit & { id?: React.Key; limitId?: React.Key }).id ??
                                    (amount as AmountLimit & { id?: React.Key; limitId?: React.Key }).limitId
                                }
                                amount={amount}
                                selectedLimitId={selectedLimit}
                                tabIndexStart={limitsSectionStart + index * TAB_INDICES_PER_LIMIT_ITEM}
                                getAmountDetails={getAmountDetails}
                                deleteAmountLimit={deleteAmountLimit}
                                saveAmountLimitName={saveAmountLimit}
                                editButtonRef={index === 0 ? firstEditButtonRef : undefined}
                                inputRef={index === 0 ? firstInputRef : undefined}
                            />
                        ))}

                        {showNewLimit && (
                            <LimitNameRow
                                isNew
                                selectedLimitId={null}
                                tabIndexStart={
                                    limitsSectionStart + limitsCount * TAB_INDICES_PER_LIMIT_ITEM
                                }
                                getAmountDetails={getAmountDetails}
                                deleteAmountLimit={deleteAmountLimit}
                                saveAmountLimitName={addAmountLimit}
                                cancelCallback={() => setShowNewLimit(false)}
                                editButtonRef={amountLimits?.length === 0 ? firstEditButtonRef : undefined}
                                inputRef={amountLimits?.length === 0 ? firstInputRef : undefined}
                            />
                        )}

                        <div className="d-flex justify-content-end p-2">
                            <Button
                                type={ButtonType.SECONDARY}
                                tabIndex={limitsAddButton}
                                ref={addLimitButtonRef}
                                onClick={() => setShowNewLimit(true)}
                                text={translate("roles.add")}
                            />
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div className="my-24">
                    <span>{translate("popUp.editLimit.dividerText")}</span>
                    <hr />
                </div>

                {/* Currency/amount details section */}
                <div className="actions-list col-md-12 px-2">
                    <div className="row px-0 mx-0">
                        <div className="col-md-3 px-0">{translate("popUp.editLimit.currency")}</div>
                        <div className="col-md-6 text-center">{translate("popUp.editLimit.value")}</div>
                        <div className="col-md-3 px-0 float-right text-end">{translate("roles.actions")}</div>
                    </div>
                    <div
                        className={
                            "col-md-12 main-panel px-0 border-0" + (showNewDetails ? " editing" : "")
                        }
                    >
                        {selectedLimit != null && (
                            <>
                                {amountDetails.map((detail, index) => (
                                    <AmountDetailRow
                                        key={index}
                                        detail={detail}
                                        id={selectedLimit}
                                        allCurrencies={allCurrencies}
                                        deleteCurrency={deleteCurrency}
                                        addCurrencyLimit={addCurrencyLimit}
                                        tabIndexStart={
                                            detailsSectionStart + index * TAB_INDICES_PER_AMOUNT_ITEM
                                        }
                                    />
                                ))}

                                {showNewDetails && (
                                    <AmountDetailRow
                                        isNew
                                        id={selectedLimit}
                                        allCurrencies={allCurrencies}
                                        deleteCurrency={deleteCurrency}
                                        addCurrencyLimit={addCurrencyLimit}
                                        tabIndexStart={
                                            detailsSectionStart +
                                            detailsCount * TAB_INDICES_PER_AMOUNT_ITEM
                                        }
                                        cancelCallback={() => setShowNewDetails(false)}
                                    />
                                )}

                                <div className="d-flex justify-content-end p-2">
                                    <Button
                                        type={ButtonType.SECONDARY}
                                        tabIndex={detailsAddButton}
                                        onClick={() => setShowNewDetails(true)}
                                        text={translate("roles.add")}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="ga-modal__actions">
                <Button
                    type={ButtonType.PRIMARY}
                    onClick={closeModal}
                    onBlur={loopFocus}
                    tabIndex={okButton}
                    text={translate("popUp.ok")}
                />
            </div>
        </>
    );
};

export default ChangeAmountLimit;

